import clsx from 'clsx';
import type { ReactNode } from 'react';
import { GoMail } from 'react-icons/go';
import { IoChatbubbleOutline, IoLocationOutline } from 'react-icons/io5';
import { LuGlobe, LuMail, LuMapPin, LuPhone, LuPhoneCall } from 'react-icons/lu';
import { RiGlobalLine } from 'react-icons/ri';
import ReactMarkdown from 'react-markdown';

import { imageVariantLink } from '@/common/media';
import { Card } from '@/components';
import ButtonSmall from '@/components/Button/ButtonSmall';
import { ChipGroup, docStyles as d, Field, SectionHeader } from '@/components/ProfileDoc';
import type { Location } from '@/store/marina-services/marina-service.dto';
import type { ServiceProviderImage } from '@/store/service-providers/service-providers.dto';
import { CAROUSEL_SLIDE_HEIGHT, CAROUSEL_SLIDE_WIDTH, Carousel } from '@components/ImageCarousel';
import { RadiusSearchMap } from '@pages/DockagePage/components/DockagesMap';

import { providerCategoryTitles, providerCertifications, providerPlaces } from './provider-facts';
import s from './ProviderRecord.module.scss';
import { ensureUrlProtocol } from './utils/ensureUrlProtocol';

/**
 * The parts a service provider's page and a marina's page draw the same way.
 *
 * They are one backend record under two names, so everything but the berthing
 * limits is shared: the document's cards, the written blocks, the services, the
 * places they work from and the map under them, the photo strip, the contact
 * facts and the row of actions that acts on them. Each page composes these and
 * adds only what is its own.
 */

type ContactInfo = {
  email?: null | string;
  phone?: null | string;
  website?: null | string;
  emergency_contact?: null | string;
};

/** One card of the document: the heading, then whatever the section holds. */
export const RecordSection = ({
  title,
  onEdit,
  children,
}: {
  title: string;
  /** Absent on a record that is not yours to change. */
  onEdit?: () => void;
  children: ReactNode;
}) => (
  <Card className={d.section}>
    <SectionHeader title={title} onEdit={onEdit} />
    {children}
  </Card>
);

/** A written block — a description, a note. Markdown, as both are stored. */
export const RecordText = ({ value, empty }: { value?: null | string; empty: string }) =>
  value?.trim() ? (
    <div className={clsx('body-medium', 'md-content', s.markdown)}>
      <ReactMarkdown>{value}</ReactMarkdown>
    </div>
  ) : (
    <p className={clsx('body-small', d.muted)}>{empty}</p>
  );

/** What they do, and what they are certified to do — chips, as a CV reads them. */
export const RecordServices = ({ locations }: { locations: Location[] }) => {
  const certifications = providerCertifications({ locations });

  return (
    <>
      <ChipGroup label="Services" items={providerCategoryTitles({ locations })} />
      {/* Left out rather than shown empty: nothing in the create form writes
          certifications, so an empty row here is the norm, not a gap to fill. */}
      {!!certifications.length && <ChipGroup label="Certifications" items={certifications} />}
    </>
  );
};

/**
 * Where they work from: the addresses on record, over the map they sit on.
 *
 * Only a real address makes a location worth printing — the backend wants a
 * location whether or not anyone picked one, so the create form sends filler for
 * the rest, and the map is drawn only once some row carries coordinates that are
 * not 0/0 (see `provider-facts`).
 */
export const RecordLocations = ({ locations }: { locations: Location[] }) => {
  const places = providerPlaces({ locations });

  const points = locations.flatMap(({ id, location }) =>
    location ? [{ id, lat: location.latitude, lng: location.longitude, name: location.name }] : [],
  );
  const mappable = points.some(
    ({ lat, lng }) => Number.isFinite(lat) && Number.isFinite(lng) && (lat !== 0 || lng !== 0),
  );

  return (
    <>
      {places.length === 0 ? (
        <p className={clsx('body-small', d.muted)}>No address provided.</p>
      ) : (
        places.map(({ id, address }) => (
          <div className={s.place} key={id}>
            <IoLocationOutline aria-hidden="true" />
            <span className={d.fieldValue}>{address}</span>
          </div>
        ))
      )}
      {mappable ? (
        <div className={s.mapFrame}>
          <RadiusSearchMap points={points} />
        </div>
      ) : (
        <div className={s.noMap}>
          <IoLocationOutline aria-hidden="true" />
          <p className="body-medium">No location provided</p>
        </div>
      )}
    </>
  );
};

/** The photo strip, as both the catalogue card and the old page called it. */
export const RecordPhotos = ({
  images,
  onPreview,
}: {
  images: ServiceProviderImage[];
  onPreview: (image: ServiceProviderImage) => void;
}) => (
  <Carousel>
    <Carousel.Prev />
    <Carousel.Track>
      {images.map((image) => (
        <Carousel.Slide
          key={image.id}
          src={imageVariantLink(image, CAROUSEL_SLIDE_WIDTH, CAROUSEL_SLIDE_HEIGHT) ?? image.link}
          alt={image.name}
          className={s.photo}
          onClick={() => onPreview(image)}
        />
      ))}
    </Carousel.Track>
    <Carousel.Next />
  </Carousel>
);

/** How to reach them, as values you can read — the row of actions only dials them. */
export const RecordContactFacts = ({ contact }: { contact?: null | ContactInfo }) => (
  <div className={d.grid}>
    <Field label="Phone" value={contact?.phone} />
    <Field label="Email" value={contact?.email} />
    <Field label="Website" value={contact?.website} />
    <Field label="Emergency Contact" value={contact?.emergency_contact} />
  </div>
);

/**
 * Where they are and how to reach them, as a list under the identity: each way
 * in is the value itself, a link that dials, writes or opens it. A row with
 * nothing behind it is left out, and with none at all the list is not drawn.
 */
export const RecordContactList = ({
  contact,
  place,
}: {
  contact?: null | ContactInfo;
  /** Where they are, already in words — the address and how far off. */
  place?: string;
}) => {
  const { phone, email, website } = contact ?? {};

  if (!place && !phone && !email && !website) return null;

  return (
    <ul className={clsx('body-medium', s.contactList)}>
      {!!place && (
        <li>
          <LuMapPin aria-hidden="true" />
          <span>{place}</span>
        </li>
      )}
      {!!phone && (
        <li>
          <LuPhone aria-hidden="true" />
          <a href={`tel:${phone}`}>{phone}</a>
        </li>
      )}
      {!!email && (
        <li>
          <LuMail aria-hidden="true" />
          <a href={`mailto:${email}`}>{email}</a>
        </li>
      )}
      {!!website && (
        <li>
          <LuGlobe aria-hidden="true" />
          <a href={ensureUrlProtocol(website)} target="_blank" rel="noreferrer">
            {/* The address as it is said, not as it is typed into a browser. */}
            {website
              .trim()
              .replace(/^https?:\/\//i, '')
              .replace(/\/$/, '')}
          </a>
        </li>
      )}
    </ul>
  );
};

/**
 * The row under the identity: the ways to reach them, and nothing that isn't
 * one. The same button a crew CV's Call and Chat are, so the two record pages
 * offer their actions at one weight.
 *
 * A button is left out rather than disabled when there is nothing behind it — a
 * dead Call button reads as a broken phone, not a missing number.
 */
export const RecordContactActions = ({
  contact,
  onChat,
  children,
}: {
  contact?: null | ContactInfo;
  /** Absent where there is no one to open a chat with. */
  onChat?: () => void;
  /** An action of the page rather than of the contact card — asking them for
      work, say. It joins the row so it wraps with it and is padded like it. */
  children?: ReactNode;
}) => {
  const { phone, email, website } = contact ?? {};

  return (
    <section className={s.contacts}>
      {!!phone && (
        <ButtonSmall onClick={() => (window.location.href = `tel:${phone}`)}>
          <LuPhoneCall />
          <span className="body-medium">Call</span>
        </ButtonSmall>
      )}
      {onChat && (
        <ButtonSmall onClick={onChat}>
          <IoChatbubbleOutline />
          <span className="body-medium">Chat</span>
        </ButtonSmall>
      )}
      {!!email && (
        <ButtonSmall onClick={() => (window.location.href = `mailto:${email}`)}>
          <GoMail />
          <span className="body-medium">Email</span>
        </ButtonSmall>
      )}
      {!!website && (
        <ButtonSmall
          onClick={() => window.open(ensureUrlProtocol(website), '_blank', 'noreferrer')}
        >
          <RiGlobalLine />
          <span className="body-medium">Site</span>
        </ButtonSmall>
      )}
      {children}
    </section>
  );
};
